import React, { CSSProperties, FormEvent, useMemo, useRef, useState } from 'react';
import { EyeOutlined, LoadingOutlined, QuestionCircleOutlined, TrophyOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import GamePlayerPortrait from '../components/GamePlayerPortrait';
import {
  WHO_AM_I_AUTOSUGGEST_MIN_CHARACTERS,
  WHO_AM_I_CLUE_VARIANTS,
  WHO_AM_I_DEFAULT_HINTS,
  WHO_AM_I_DEFAULT_MINIMUM_STARTS,
  WHO_AM_I_EXPORT_MINIMUM_STARTS,
  WHO_AM_I_MAX_HINTS,
  WHO_AM_I_MIN_HINTS,
  buildFallbackWhoAmIClues,
  searchWhoAmIPlayers,
  selectWhoAmIPlayer,
} from '../games/whoAmILogic';
import { useGamesData } from '../hooks/useGamesData';
import { useWhoAmIClues } from '../hooks/useWhoAmIClues';
import { WhoAmIClue, WhoAmIPlayer } from '../types/games';
import './Games.css';

type RoundPhase = 'setup' | 'loading' | 'playing' | 'won' | 'lost';

const MAX_PHOTO_BLUR = 24;
const MINIMUM_START_OPTIONS = [20, 30, 40, 50, 75, 100];

const WhoAmIGame: React.FC = () => {
  const { data, isLoading: isDataLoading, error: dataError } = useGamesData();
  const { generateClues } = useWhoAmIClues();
  const [phase, setPhase] = useState<RoundPhase>('setup');
  const [answer, setAnswer] = useState<WhoAmIPlayer>();
  const [clues, setClues] = useState<WhoAmIClue[]>([]);
  const [guesses, setGuesses] = useState<WhoAmIPlayer[]>([]);
  const [skipCount, setSkipCount] = useState(0);
  const [query, setQuery] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState<WhoAmIPlayer>();
  const [clueNote, setClueNote] = useState('');
  const [hintCount, setHintCount] = useState(WHO_AM_I_DEFAULT_HINTS);
  const [minimumStarts, setMinimumStarts] = useState(WHO_AM_I_DEFAULT_MINIMUM_STARTS);
  const [photoBlurEnabled, setPhotoBlurEnabled] = useState(true);
  const roundRef = useRef(0);
  const players = useMemo(() => data?.WhoAmIPlayers || [], [data?.WhoAmIPlayers]);
  const eligiblePlayers = useMemo(
    () => players.filter((player) => player.GamesStarted >= minimumStarts),
    [players, minimumStarts],
  );
  const guessedIds = useMemo(() => new Set(guesses.map((guess) => guess.PlayerId)), [guesses]);
  const attempts = guesses.length + skipCount;
  const suggestions = useMemo(
    () => searchWhoAmIPlayers(eligiblePlayers, query, guessedIds),
    [eligiblePlayers, query, guessedIds],
  );
  const exactMatch = useMemo(() => eligiblePlayers.find((player) => !guessedIds.has(player.PlayerId)
    && player.Name.toLocaleLowerCase() === query.trim().toLocaleLowerCase()), [eligiblePlayers, query, guessedIds]);

  const startRound = async () => {
    const nextAnswer = selectWhoAmIPlayer(eligiblePlayers);
    if (!nextAnswer) return;
    const clueVariant = Math.floor(Math.random() * WHO_AM_I_CLUE_VARIANTS);
    const round = roundRef.current + 1;
    roundRef.current = round;
    setAnswer(nextAnswer);
    setClues(buildFallbackWhoAmIClues(nextAnswer, hintCount, clueVariant));
    setGuesses([]);
    setSkipCount(0);
    setQuery('');
    setSelectedPlayer(undefined);
    setClueNote('');
    setPhase('loading');

    try {
      const generated = await generateClues(nextAnswer, hintCount, clueVariant);
      if (roundRef.current !== round) return;
      setClues(generated);
    } catch {
      if (roundRef.current !== round) return;
      setClueNote('The Historian is offline, so this round uses archive-generated hints.');
    }
    if (roundRef.current === round) setPhase('playing');
  };

  const submitGuess = (event: FormEvent) => {
    event.preventDefault();
    if (phase !== 'playing' || !answer) return;
    const guess = selectedPlayer || exactMatch;
    if (!guess || guessedIds.has(guess.PlayerId)) return;

    const nextGuesses = [...guesses, guess];
    const nextAttemptCount = attempts + 1;
    setGuesses(nextGuesses);
    setQuery('');
    setSelectedPlayer(undefined);
    if (guess.PlayerId === answer.PlayerId) {
      setPhase('won');
    } else if (nextAttemptCount >= hintCount) {
      setPhase('lost');
    }
  };

  const skipGuess = () => {
    if (phase !== 'playing') return;
    const nextAttemptCount = attempts + 1;
    setSkipCount((count) => count + 1);
    setQuery('');
    setSelectedPlayer(undefined);
    if (nextAttemptCount >= hintCount) setPhase('lost');
  };

  if (isDataLoading) {
    return <div className="games-page who-am-i-status"><LoadingOutlined /> Loading the mystery player pool…</div>;
  }
  if (dataError || players.length === 0) {
    return <div className="games-page who-am-i-status">The Who Am I player pool is not available yet.</div>;
  }

  if (phase === 'setup') {
    return (
      <div className="games-page who-am-i-page">
        <Link to="/games" className="games-back">← All games</Link>
        <header className="games-hero games-hero--compact">
          <div className="games-eyebrow"><QuestionCircleOutlined /> Configurable hints · new player every round</div>
          <h1>Who Am I?</h1>
          <p>The GL Historian mixes league history with NFL hints. Every wrong guess reveals another hint and brings the player into focus.</p>
        </header>
        <section className="game-setup-card who-am-i-setup">
          <div className="who-am-i-rule-grid">
            <span><strong>{minimumStarts}+</strong> archive starts</span>
            <span><strong>{hintCount}</strong> identity hints</span>
            <span><strong>3</strong> letters to search</span>
          </div>
          <div className="who-am-i-settings">
            <label>
              <span>Number of hints</span>
              <select value={hintCount} onChange={(event) => setHintCount(Number(event.target.value))}>
                {Array.from(
                  { length: WHO_AM_I_MAX_HINTS - WHO_AM_I_MIN_HINTS + 1 },
                  (_, index) => WHO_AM_I_MIN_HINTS + index,
                ).map((count) => <option key={count} value={count}>{count} hints / guesses</option>)}
              </select>
            </label>
            <label>
              <span>Minimum fantasy starts</span>
              <select value={minimumStarts} onChange={(event) => setMinimumStarts(Number(event.target.value))}>
                {MINIMUM_START_OPTIONS.filter((count) => count >= WHO_AM_I_EXPORT_MINIMUM_STARTS)
                  .map((count) => <option key={count} value={count}>{count}+ starts</option>)}
              </select>
            </label>
            <label className="who-am-i-toggle">
              <input
                type="checkbox"
                checked={photoBlurEnabled}
                onChange={(event) => setPhotoBlurEnabled(event.target.checked)}
              />
              <span>Show a blurred photo that clears between guesses</span>
            </label>
          </div>
          <p>{eligiblePlayers.length} players meet these settings. Players with an available photo are selected first.</p>
          <button type="button" className="game-primary-button" onClick={startRound} disabled={eligiblePlayers.length === 0}>Start a mystery</button>
        </section>
      </div>
    );
  }

  if (!answer) return null;
  if (phase === 'loading') {
    return (
      <div className="games-page who-am-i-page">
        <Link to="/games" className="games-back">← All games</Link>
        <section className="who-am-i-loading" aria-live="polite">
          <LoadingOutlined />
          <h1>The Historian is writing your hints…</h1>
          <p>Balancing Gibsons League history with NFL knowledge.</p>
        </section>
      </div>
    );
  }

  const hasEnded = phase === 'won' || phase === 'lost';
  const revealedClueCount = hasEnded ? clues.length : Math.min(attempts + 1, clues.length);
  const showPortrait = hasEnded || photoBlurEnabled;
  const photoBlur = hasEnded || !photoBlurEnabled
    ? 0
    : Math.round(MAX_PHOTO_BLUR * Math.max(0, 1 - (attempts / hintCount)));
  const photoClarity = Math.round((1 - photoBlur / MAX_PHOTO_BLUR) * 100);
  const photoStyle = { '--who-photo-blur': `${photoBlur}px` } as CSSProperties;
  const primaryFranchise = [...answer.Franchises].sort((left, right) => right.GamesStarted - left.GamesStarted)[0];

  return (
    <div className="games-page who-am-i-page">
      <div className="who-am-i-topbar">
        <Link to="/games" className="games-back">← All games</Link>
        <span>{hasEnded ? 'Round complete' : `Attempt ${attempts + 1} of ${hintCount}`}</span>
      </div>

      <div className="who-am-i-board">
        <section className="who-am-i-identity">
          <div className={`who-am-i-photo ${hasEnded ? 'is-revealed' : ''} ${showPortrait ? '' : 'is-hidden'}`} style={photoStyle}>
            {showPortrait ? (
              <GamePlayerPortrait name={answer.Name} position={answer.Position} url={answer.HeadshotUrl} />
            ) : (
              <div className="who-am-i-photo-placeholder" aria-label="Player photo hidden until the round ends">
                <QuestionCircleOutlined />
                <span>Photo hidden</span>
              </div>
            )}
          </div>
          {photoBlurEnabled ? (
            <>
              <div className="who-am-i-focus-meter" aria-label={`Photo clarity ${photoClarity} percent`}>
                <span style={{ width: `${photoClarity}%` }} />
              </div>
              <small><EyeOutlined /> The portrait clears after every guess</small>
            </>
          ) : <small><EyeOutlined /> {hasEnded ? 'Portrait revealed' : 'The portrait will appear after the round'}</small>}

          {hasEnded && (
            <div className={`who-am-i-answer ${phase === 'won' ? 'is-win' : 'is-loss'}`}>
              {phase === 'won' ? <TrophyOutlined /> : <QuestionCircleOutlined />}
              <span>{phase === 'won' ? 'You got it' : 'The mystery player was'}</span>
              <h1>{answer.Name}</h1>
              <p>{answer.Position} · {answer.GamesStarted} starts · {answer.PointsPerGame.toFixed(1)} PPG</p>
              {primaryFranchise && <small>Most starts: {primaryFranchise.Name} ({primaryFranchise.GamesStarted})</small>}
            </div>
          )}
        </section>

        <section className="who-am-i-clues" aria-live="polite">
          <header>
            <span>Hint board</span>
            <strong>{revealedClueCount} / {clues.length}</strong>
          </header>
          {clueNote && <p className="who-am-i-note">{clueNote}</p>}
          <ol>
            {clues.map((clue, index) => (
              <li key={`${clue.hint}-${index}`} className={index < revealedClueCount ? 'is-visible' : 'is-locked'}>
                <span>{index + 1}</span>
                {index < revealedClueCount ? (
                  <div>
                    <small>{clue.source === 'archive' ? 'League archive' : 'NFL history'}</small>
                    <p>{clue.hint}</p>
                  </div>
                ) : <em>Locked until the next wrong guess</em>}
              </li>
            ))}
          </ol>

          {!hasEnded ? (
            <form className="who-am-i-guess" onSubmit={submitGuess}>
              <label htmlFor="who-am-i-player">Who is the player?</label>
              <div className="who-am-i-input-row">
                <input
                  id="who-am-i-player"
                  type="text"
                  value={query}
                  autoComplete="off"
                  placeholder={`Type at least ${WHO_AM_I_AUTOSUGGEST_MIN_CHARACTERS} characters`}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setSelectedPlayer(undefined);
                  }}
                />
                <button type="submit" className="game-primary-button" disabled={!selectedPlayer && !exactMatch}>Guess</button>
                <button
                  type="button"
                  className="game-secondary-button who-am-i-skip"
                  onClick={skipGuess}
                  title="Use an attempt and reveal the next clue"
                >Skip</button>
              </div>
              {suggestions.length > 0 && (
                <div className="who-am-i-suggestions" role="listbox" aria-label="Eligible players">
                  {suggestions.map((player) => (
                    <button
                      key={player.PlayerId}
                      type="button"
                      className={selectedPlayer?.PlayerId === player.PlayerId ? 'is-selected' : ''}
                      onClick={() => {
                        setQuery(player.Name);
                        setSelectedPlayer(player);
                      }}
                    >
                      <strong>{player.Name}</strong>
                      <span>{player.Position}</span>
                    </button>
                  ))}
                </div>
              )}
              {(guesses.length > 0 || skipCount > 0) && (
                <div className="who-am-i-past-guesses">
                  <span>Previous attempts</span>
                  {guesses.map((guess) => <del key={guess.PlayerId}>{guess.Name}</del>)}
                  {skipCount > 0 && <em>Skipped × {skipCount}</em>}
                </div>
              )}
            </form>
          ) : (
            <div className="game-result-actions">
              <button type="button" className="game-primary-button" onClick={startRound}>Play again</button>
              <button type="button" className="game-secondary-button" onClick={() => setPhase('setup')}>Change settings</button>
              <Link to="/games" className="game-secondary-button">All games</Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default WhoAmIGame;
